import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";
import EMark from "@/components/EMark";

/**
 * useInView — sets `inView` to true once the element first crosses the
 * viewport threshold. Stays true thereafter (animation plays once).
 */
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/**
 * OrbitCloseDispatch — Alternative A
 * --------------------------------------------------------------------------
 * Concept: "The Dispatch from the Centre."
 * A quiet, official transmission leaving the central structure. Reads as a
 * telex / dispatch slip: a typed eyebrow, three short editorial lines that
 * appear one after the other with a typewriter cadence, a blinking cursor,
 * and a single signature line below — Erenown's coordinates and date.
 *
 * Premise: nothing repeated from above the page. No brand list, no
 * registry rows, no orbit graphic. Pure editorial restraint with a
 * functional, transmissional character. Feels like a closing note
 * dispatched outward from the centre, not a list of what was already shown.
 * --------------------------------------------------------------------------
 */
const DISPATCH_LINES = [
  "The orbit, once described, is not a list.",
  "It is a posture — held in one direction, over time.",
  "The centre is not a position. It is a discipline.",
];

function useTypewriter(lines, start, charDelay = 22, lineGap = 380) {
  const [shown, setShown] = useState([""]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!start) return;
    let cancelled = false;
    let timers = [];
    const advance = (lineIdx, charIdx) => {
      if (cancelled) return;
      const current = lines[lineIdx];
      if (charIdx <= current.length) {
        setShown((prev) => {
          const next = [...prev];
          next[lineIdx] = current.slice(0, charIdx);
          return next;
        });
        timers.push(setTimeout(() => advance(lineIdx, charIdx + 1), charDelay));
      } else if (lineIdx < lines.length - 1) {
        setShown((prev) => [...prev, ""]);
        timers.push(setTimeout(() => advance(lineIdx + 1, 0), lineGap));
      } else {
        setDone(true);
      }
    };
    timers.push(setTimeout(() => advance(0, 0), 600));
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [lines, start, charDelay, lineGap]);

  return { shown, done };
}

export function OrbitCloseDispatch() {
  const [sectionRef, inView] = useInView(0.2);
  const { shown, done } = useTypewriter(DISPATCH_LINES, inView);

  return (
    <section
      ref={sectionRef}
      data-testid="orbit-close-dispatch"
      className="relative overflow-hidden border-t"
      style={{ borderColor: T.border, background: T.bg }}
    >
      {/* Faint vertical hairlines — like a printout frame */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: `linear-gradient(to right, ${T.text} 1px, transparent 1px)`,
          backgroundSize: "240px 100%",
          maskImage: "linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)",
        }}
      />
      {/* Single quiet warm wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 30% 50% at 95% 50%, ${T.warm}10, transparent 70%)`,
        }}
      />

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 py-28 md:py-36">
        {/* Dispatch eyebrow */}
        <div className="flex items-center gap-5 font-plexmono text-[10.5px] tracking-[0.36em] uppercase" style={{ color: T.muted }}>
          <span className="inline-flex items-center gap-2"><EMark /> · DISPATCH · 001</span>
          <span className="flex-1 h-px" style={{ background: T.border }} />
          <span style={{ color: T.warm }}>From the Centre</span>
        </div>

        {/* Dispatch body — typed lines */}
        <div className="mt-20 md:mt-24 space-y-7 md:space-y-9">
          {DISPATCH_LINES.map((line, i) => {
            const text = shown[i] || "";
            const isCurrent = i === shown.length - 1 && !done;
            return (
              <div
                key={i}
                className="font-fraunces font-light leading-[1.18] tracking-[-0.005em]"
                style={{
                  color: T.text,
                  fontSize: "clamp(28px, 3.6vw, 48px)",
                  fontVariationSettings: "'SOFT' 50",
                  minHeight: "1.18em",
                }}
              >
                {text}
                {isCurrent && (
                  <span
                    aria-hidden
                    className="inline-block align-baseline ml-2"
                    style={{
                      width: "0.55ch",
                      height: "0.95em",
                      background: T.warm,
                      animation: "node-pulse 1.2s ease-in-out infinite",
                      transform: "translateY(0.08em)",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Signature strip */}
        <div className="mt-20 md:mt-24 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7">
            <div
              className="border-t pt-6 font-plexmono text-[10.5px] tracking-[0.32em] uppercase leading-[1.95]"
              style={{ borderColor: T.border, color: T.muted }}
            >
              <div>Signed · <span style={{ color: T.warm }}>Erenown</span> · Central Structure</div>
              <div>Field · 51.4769°N · 0.0005°W</div>
              <div>Sent · MMXX</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <Link
              to="/contact"
              data-testid="dispatch-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{ borderColor: `${T.warm}66`, color: T.warm }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${T.warm}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Reply to the centre
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * OrbitCloseTransit — Alternative B
 * --------------------------------------------------------------------------
 * Concept: "The Transit Log."
 * A premium horizontal timeline reading like an astronomical transit sheet.
 * One thin axis crosses the section. Five small notches mark moments along
 * it — but the notches are unlabeled (no brand names; the brands are above
 * already). Above the axis, a slow scan-line dot moves from left to right
 * once on enter and settles. Below the axis, two short editorial captions:
 * one in editorial serif, one in mono.
 *
 * The single signature is the warm "E" centred at the right end of the axis
 * — implying that all transits resolve to the same centre. No registry
 * list, no brand names. A graceful, low-noise closing.
 * --------------------------------------------------------------------------
 */
export function OrbitCloseTransit() {
  const [sectionRef, inView] = useInView(0.2);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = Date.now();
    const dur = 4200;
    const tick = () => {
      const elapsed = Date.now() - start;
      const t = Math.min(1, elapsed / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    const startId = setTimeout(() => { raf = requestAnimationFrame(tick); }, 500);
    return () => { clearTimeout(startId); cancelAnimationFrame(raf); };
  }, [inView]);

  const VB_W = 1200;
  const VB_H = 220;
  const padX = 80;
  const axisY = 110;
  const ticks = [0, 1, 2, 3, 4].map((i) => padX + (i * (VB_W - padX * 2)) / 5);
  const endX = VB_W - padX;
  const dotX = padX + progress * (endX - padX);

  return (
    <section
      ref={sectionRef}
      data-testid="orbit-close-transit"
      className="relative overflow-hidden border-t"
      style={{ borderColor: T.border, background: T.bg }}
    >
      {/* Subtle dotted veil */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: `radial-gradient(circle at center, ${T.text} 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          maskImage: "linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 35% 55% at 88% 50%, ${T.warm}12, transparent 70%)`,
        }}
      />

      <div className="relative max-w-[1300px] mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Header */}
        <div className="flex items-center gap-5 font-plexmono text-[10.5px] tracking-[0.36em] uppercase" style={{ color: T.muted }}>
          <span className="inline-flex items-center gap-2"><EMark /> · TRANSIT · 001</span>
          <span className="flex-1 h-px" style={{ background: T.border }} />
          <span style={{ color: T.warm }}>One bearing · MMXX</span>
        </div>

        {/* Headline */}
        <h2
          className="mt-12 md:mt-16 max-w-3xl font-fraunces font-light text-4xl md:text-5xl lg:text-[60px] leading-[1.04] tracking-[-0.005em]"
          style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
          data-testid="transit-headline"
        >
          The orbit, in <span className="italic" style={{ color: T.accent }}>transit</span>
          <span style={{ color: T.warm }}>.</span>
        </h2>

        {/* Transit visual */}
        <div className="mt-16 md:mt-20" data-testid="transit-axis">
          <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="ot-axis" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"  stopColor={T.borderSoft} stopOpacity="0.3" />
                <stop offset="40%" stopColor={T.accent}     stopOpacity="0.55" />
                <stop offset="100%" stopColor={T.warm}      stopOpacity="0.85" />
              </linearGradient>
              <radialGradient id="ot-dot" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor={T.accent} stopOpacity="0.95" />
                <stop offset="60%" stopColor={T.accent} stopOpacity="0.25" />
                <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
              </radialGradient>
              <radialGradient id="ot-anchor" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor={T.warm} stopOpacity="0.95" />
                <stop offset="60%" stopColor={T.warm} stopOpacity="0.25" />
                <stop offset="100%" stopColor={T.warm} stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* The axis itself */}
            <line x1={padX} y1={axisY} x2={endX} y2={axisY} stroke="url(#ot-axis)" strokeWidth="1.4" strokeLinecap="round" />

            {/* Five small unlabeled tick marks */}
            {ticks.map((x, i) => (
              <g key={i}>
                <line x1={x} y1={axisY - 8} x2={x} y2={axisY + 8} stroke={T.borderSoft} strokeWidth="1" />
                <text
                  x={x} y={axisY - 18} textAnchor="middle"
                  fontFamily="IBM Plex Mono, monospace" fontSize="9" letterSpacing="3" fill={T.muted}
                  style={{ textTransform: "uppercase" }}
                >
                  0{i + 1}
                </text>
              </g>
            ))}

            {/* Travelling scan dot */}
            <g style={{ transform: `translateX(${dotX - VB_W / 2}px)`, transformOrigin: "center", transformBox: "fill-box" }}>
              <circle cx={VB_W / 2} cy={axisY} r="14" fill="url(#ot-dot)" />
              <circle cx={VB_W / 2} cy={axisY} r="3.2" fill={T.accent} />
            </g>

            {/* Warm anchor at the right end */}
            <g>
              <circle cx={endX} cy={axisY} r="22" fill="url(#ot-anchor)" />
              <circle cx={endX} cy={axisY} r="4.5" fill={T.warm} />
              <circle cx={endX} cy={axisY} r="11" fill="none" stroke={T.warm} strokeOpacity="0.5" strokeWidth="0.8" />
            </g>

            {/* Cardinal label below the warm anchor */}
            <text
              x={endX} y={axisY + 36} textAnchor="middle"
              fontFamily="IBM Plex Mono, monospace" fontSize="10" letterSpacing="3" fill={T.warm}
              style={{ textTransform: "uppercase" }}
            >
              Centre
            </text>
          </svg>
        </div>

        {/* Captions row */}
        <div className="mt-12 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="max-w-xl font-intertight text-[15.5px] leading-[1.95]" style={{ color: T.textSoft }}>
              Five lines, one bearing. They move at their own pace, in their own field — yet each transit
              resolves to the same centre. The orbit is not a place; it is the standing direction in which
              Erenown is held.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <Link
              to="/contact"
              data-testid="transit-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{ borderColor: `${T.warm}66`, color: T.warm }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${T.warm}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Speak to the centre
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===========================================================
 * Preview page — shows both alternatives stacked, with labels.
 * Route: /orbit-close-preview
 * =========================================================== */
export default function OrbitClosePreview() {
  return (
    <div data-testid="orbit-close-preview" style={{ background: T.bg, color: T.text }}>
      {/* Intro */}
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
          }}
        />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>
            <span className="inline-flex items-center gap-2"><EMark /> · ORBIT · CLOSE · OPTIONS</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>02 alternatives · review</span>
          </div>
          <h1 className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[68px] leading-[1.04] tracking-[-0.015em] mt-10 max-w-3xl" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
            Two ways to close the <span className="italic" style={{ color: T.accent }}>Orbit</span>.
          </h1>
          <p className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
            Both replace the current "Erenown Register" with a more premium, less repetitive ending — neither restates the brand list. Compare them in real context below. The current register stays in place on /orbit until you choose.
          </p>
        </div>
      </section>

      {/* Option A label */}
      <div className="border-t" style={{ borderColor: T.border, background: T.surface }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-10 flex flex-wrap items-baseline gap-x-8 gap-y-3">
          <div className="font-plexmono text-[10.5px] tracking-[0.32em] uppercase" style={{ color: T.warm }}>
            Option A
          </div>
          <h2 className="font-fraunces font-light text-3xl md:text-4xl" style={{ color: T.text }}>
            The Dispatch from the Centre
          </h2>
          <p className="basis-full mt-2 max-w-2xl font-intertight text-[14px] leading-[1.85]" style={{ color: T.textSoft }}>
            A quiet, official transmission leaving the centre. Three short serif lines type out one after the other with a blinking warm cursor. Signed at the foot with Erenown's coordinates and date. No brand list, no orbit graphic. Pure editorial restraint with a deliberate, transmissional character.
          </p>
        </div>
      </div>
      <OrbitCloseDispatch />

      {/* Option B label */}
      <div className="border-t" style={{ borderColor: T.border, background: T.surface }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-10 flex flex-wrap items-baseline gap-x-8 gap-y-3">
          <div className="font-plexmono text-[10.5px] tracking-[0.32em] uppercase" style={{ color: T.warm }}>
            Option B
          </div>
          <h2 className="font-fraunces font-light text-3xl md:text-4xl" style={{ color: T.text }}>
            The Transit Log
          </h2>
          <p className="basis-full mt-2 max-w-2xl font-intertight text-[14px] leading-[1.85]" style={{ color: T.textSoft }}>
            A premium horizontal timeline reading like an astronomical transit sheet. A thin gradient axis crosses the section; five small unlabeled tick marks (no names — they are above already) and a slow scan dot travelling once from left to right toward a warm anchor at the right end. A single editorial caption beneath. Calm, geometric, silent.
          </p>
        </div>
      </div>
      <OrbitCloseTransit />

      {/* Decision strip */}
      <section className="border-t" style={{ borderColor: T.border, background: T.bg }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-12 flex flex-wrap items-end justify-between gap-6">
          <p className="max-w-xl font-intertight text-[14.5px] leading-[1.85]" style={{ color: T.textSoft }}>
            Tell me which you prefer (A, B, or "keep current") and I'll wire it into the Orbit page in place of the register. If neither feels right, the existing register stays — no harm done.
          </p>
          <Link
            to="/orbit"
            className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase border transition-colors"
            style={{ borderColor: `${T.warm}66`, color: T.warm }}
          >
            Compare with current
          </Link>
        </div>
      </section>
    </div>
  );
}
